import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ArrowRight, X } from 'lucide-react';
import usePlanFeatures from '../hooks/usePlanFeatures';

// Suggests the next plan's biggest unlock based on the current plan.
// Self-hides for Pro / lifetime / trial (already-full) and when the
// user dismisses it (sessionStorage flag — comes back next session).
const NEXT_PLAN_PITCH = {
  starter: { name: 'Core',  unlock: 'Scheduling & Calendar',  blurb: 'Stop juggling jobs in your head — drag-and-drop them onto a calendar.' },
  core:    { name: 'Plus',  unlock: 'Bookkeeping & Documents', blurb: 'Track expenses, attach receipts, and upload signed contracts in one searchable vault.' },
  plus:    { name: 'Scale', unlock: 'Payroll for your team',   blurb: 'Run bulk pay for hourly crew and subcontractors with 1099 prep built-in.' },
  scale:   { name: 'Pro',   unlock: 'Workorders & Field Dispatch', blurb: 'Send jobs to field techs by SMS link — they accept, complete, and upload photos from the road.' },
};

const DISMISS_KEY = 'mantle.upgradeNudge.dismissed.v1';

export default function UpgradeNudge() {
  const { plan_id, loading } = usePlanFeatures();
  const navigate = useNavigate();
  const [hidden, setHidden] = React.useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });

  if (loading || hidden) return null;
  // Pro / comp / trial workspaces don't see this nudge.
  if (!plan_id || plan_id === 'pro' || plan_id === 'free_lifetime' || plan_id === 'trial') return null;

  const pitch = NEXT_PLAN_PITCH[plan_id];
  if (!pitch) return null;

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
    setHidden(true);
  };

  return (
    <div data-testid="upgrade-nudge" className="relative bg-gradient-to-br from-[#1B2838] via-[#1f2f42] to-[#1B2838] border border-[#B98A2C]/25 rounded-xl p-4 sm:p-5 overflow-hidden">
      <div className="absolute -top-12 -right-12 w-40 h-40 bg-[#B98A2C]/15 blur-3xl rounded-full pointer-events-none" />
      <button
        data-testid="upgrade-nudge-dismiss"
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute top-2.5 right-2.5 text-[#758A9F] hover:text-white p-1 rounded-md transition-colors"
      >
        <X size={14} />
      </button>
      <div className="relative flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#CBA33B] to-[#B98A2C] flex items-center justify-center flex-shrink-0 shadow-md">
            <Sparkles size={18} className="text-white" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#B98A2C] mb-0.5">Unlock with {pitch.name}</p>
            <p className="text-sm font-semibold text-white tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {pitch.unlock}
            </p>
            <p className="text-[11px] text-[#A8B8CA] mt-0.5 leading-snug">{pitch.blurb}</p>
          </div>
        </div>
        <button
          data-testid="upgrade-nudge-cta"
          onClick={() => navigate('/settings?section=billing')}
          className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-r from-[#CBA33B] to-[#B98A2C] hover:from-[#D6B04F] hover:to-[#C99A35] text-white text-xs font-semibold shadow-md transition-all active:scale-[0.98] flex-shrink-0"
        >
          See plans
          <ArrowRight size={13} />
        </button>
      </div>
    </div>
  );
}
